import { useState, useEffect, useMemo } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { Ticket, RefreshCw, Flame, LayoutGrid } from "lucide-react";
import { Button, EmptyState, FilterBar, Pagination, SearchField } from "@/components/ui";
import { PageFrame, ListResultsCard, ErrorState } from "@/components/common";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  TicketStatsGrid,
  TicketListItem,
  PRIORITY_CONFIG,
  CATEGORY_CONFIG,
  ticketPriorityLabel,
  ticketCategoryLabel,
  parseTicketListSearchParams,
  buildTicketListApiParams,
  getSelectedListView,
  type TicketStatus,
  type TicketPriority,
  type TicketCategory,
  type TicketListView,
} from "@/components/tickets";
import {
  supportTicketKeys,
  supportTicketService,
} from "@/services/supportTicketService";

export default function AdminTicketsPage() {
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { t } = useTranslation("support");
  const [searchParams, setSearchParams] = useSearchParams();

  const { statusFilter, view } = parseTicketListSearchParams(searchParams);
  const selectedView = getSelectedListView(statusFilter, view);
  const priorityParam = searchParams.get("priority");
  const priorityFilter = priorityParam
    ? (parseInt(priorityParam, 10) as TicketPriority)
    : null;
  const categoryFilter =
    (searchParams.get("category") as TicketCategory | null) || null;
  const searchQuery = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = 20;

  useEffect(() => {
    if (searchQuery !== debouncedSearch) {
      setSearchInput(searchQuery);
    }
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentSearch = prev.get("search") || "";
        if (debouncedSearch === currentSearch) {
          return prev;
        }
        const newParams = new URLSearchParams(prev);
        if (debouncedSearch) {
          newParams.set("search", debouncedSearch);
        } else {
          newParams.delete("search");
        }
        newParams.set("page", "1");
        return newParams;
      },
      { replace: true },
    );
  }, [debouncedSearch, setSearchParams]);

  const listParams = buildTicketListApiParams(statusFilter, view, {
    skip: (page - 1) * limit,
    limit,
    priority: priorityFilter || undefined,
    category: categoryFilter || undefined,
    search: searchQuery || undefined,
  });

  const {
    data: ticketsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: supportTicketKeys.adminList(listParams),
    queryFn: () => supportTicketService.getAllTickets(listParams),
    placeholderData: keepPreviousData,
  });

  const { data: stats } = useQuery({
    queryKey: supportTicketKeys.adminStats(),
    queryFn: () => supportTicketService.getStats(),
  });

  const tickets = ticketsData?.items ?? [];
  const total = ticketsData?.total ?? 0;

  const updateFilter = (key: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const updateView = (nextView: TicketListView) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("status");
    if (nextView === "ongoing") {
      newParams.delete("view");
    } else {
      newParams.set("view", nextView);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const updateStatus = (status: TicketStatus | null) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("view");
    if (status) {
      newParams.set("status", status);
    } else {
      newParams.delete("status");
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchInput("");
    setSearchParams(new URLSearchParams());
  };

  const totalPages = Math.ceil(total / limit);
  const hasActiveFilters =
    statusFilter !== null ||
    view === "all" ||
    priorityFilter !== null ||
    categoryFilter !== null ||
    searchQuery.length > 0;

  const priorityOptions = useMemo(
    () => [
      { value: null, label: t("adminList.filterAny") },
      ...(
        Object.entries(PRIORITY_CONFIG) as [
          string,
          (typeof PRIORITY_CONFIG)[TicketPriority],
        ][]
      ).map(([value, config]) => ({
        value: parseInt(value, 10) as TicketPriority,
        label: ticketPriorityLabel(parseInt(value, 10) as TicketPriority),
        icon: config.icon,
        iconBadgeClassName: config.color,
      })),
    ],
    [t],
  );

  const categoryOptions = useMemo(
    () => [
      { value: null, label: t("adminList.filterAny") },
      ...(
        Object.entries(CATEGORY_CONFIG) as [
          TicketCategory,
          (typeof CATEGORY_CONFIG)[TicketCategory],
        ][]
      ).map(([value, config]) => ({
        value,
        label: `${config.emoji} ${ticketCategoryLabel(value)}`,
      })),
    ],
    [t],
  );

  return (
    <PageFrame
      title={t("adminHub.ticketsTab")}
      description={t("adminHub.ticketsDescription")}
      icon={Ticket}
      actions={
        <Button
          variant="outline"
          isLoading={isFetching}
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void refetch()}
        >
          {tCommon("refresh")}
        </Button>
      }
    >
      <div className="space-y-3">
        <TicketStatsGrid
          stats={stats}
          selectedStatus={statusFilter}
          selectedView={selectedView}
          onStatusSelect={updateStatus}
          onViewSelect={updateView}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t("adminList.searchPlaceholder")}
            searchLabel={tCommon("search")}
            clearSearchLabel={tCommon("clearSearch")}
            className="min-w-0 max-w-none sm:min-w-[260px] sm:flex-1 lg:max-w-md"
          />
          <FilterBar>
            <AdminFilterMenu<TicketPriority | null>
              icon={Flame}
              name={t("adminList.priority")}
              value={priorityFilter}
              defaultValue={null}
              onChange={(priority) =>
                updateFilter(
                  "priority",
                  priority != null ? String(priority) : null,
                )
              }
              options={priorityOptions}
            />
            <AdminFilterMenu<TicketCategory | null>
              icon={LayoutGrid}
              name={t("adminList.category")}
              value={categoryFilter}
              defaultValue={null}
              onChange={(category) => updateFilter("category", category)}
              options={categoryOptions}
            />
          </FilterBar>
        </div>
      </div>

      <ListResultsCard
        title={t("adminList.ticketsFound", { count: total })}
        isLoading={isLoading}
        loadingMessage={t("adminList.loadingTickets")}
        isError={isError}
        errorState={
          <ErrorState
            message={t("list.loadError")}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
          />
        }
        isEmpty={!isLoading && !isError && tickets.length === 0}
        emptyState={
          <EmptyState
            icon={Ticket}
            title={t("adminList.noTicketsFound")}
            description={
              hasActiveFilters
                ? t("adminList.tryAdjusting")
                : t("adminList.allCaughtUp")
            }
            action={
              hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                  size="sm"
                >
                  {t("adminList.clearFilters")}
                </Button>
              )
            }
          />
        }
        pagination={
          totalPages > 1 ? (
            <Pagination
              labels={tableLabels}
              placement="bottom"
              page={page}
              totalPages={totalPages}
              onPageChange={(nextPage) =>
                updateFilter("page", String(nextPage))
              }
              showPageNumbers
              isLoading={isFetching}
              total={total}
            />
          ) : null
        }
        contentClassName="p-0"
      >
        <div className="divide-y divide-surface-200/70">
          {tickets.map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={{
                id: ticket.id,
                ticket_number: ticket.ticket_number,
                subject: ticket.subject,
                status: ticket.status,
                priority: ticket.priority,
                category: ticket.category,
                owner_name: ticket.owner_name,
                message_count: ticket.message_count,
                created_at: ticket.created_at,
                last_message_at: ticket.last_message_at,
              }}
              to={`/admin/tickets/${ticket.id}`}
              showOwner
            />
          ))}
        </div>
      </ListResultsCard>
    </PageFrame>
  );
}
